import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { useMutation } from '@apollo/client'
import { LEGAL_FORMALITY_LEGAL_DEPOSIT_UPDATE_MUTATION } from 'graphql/LegalFormalities'
import { LegalDepositType } from 'types'
import DepositContentEditor from './DepositContentEditor'

type DepositFormValues = {
  siren: string
  companyName: string
  formeJuridique: string
  numNat: string
  typeFormalite: string
  providerRef: string
  status: string
  regularisation: boolean
}

const DepositEditForm = ({
  deposit,
  onCancel,
}: {
  deposit: LegalDepositType
  onCancel: () => void
}) => {
  const [values, setValues] = useState<DepositFormValues>({
    siren: deposit.siren ?? '',
    companyName: deposit.companyName ?? '',
    formeJuridique: deposit.formeJuridique ?? '',
    numNat: deposit.numNat ?? '',
    typeFormalite: deposit.typeFormalite ?? '',
    providerRef: deposit.providerRef ?? '',
    status: deposit.status ?? '',
    regularisation: deposit.regularisation ?? false,
  })

  const [content, setContent] = useState<Record<string, unknown>>(
    (deposit.content as Record<string, unknown>) ?? {}
  )

  const [updateDeposit, { loading, error }] = useMutation(
    LEGAL_FORMALITY_LEGAL_DEPOSIT_UPDATE_MUTATION,
    { onCompleted: onCancel }
  )

  const set =
    (field: keyof DepositFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setValues(v => ({ ...v, [field]: e.target.value }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateDeposit({
      variables: {
        id: parseInt(deposit.id),
        siren: values.siren || null,
        companyName: values.companyName || null,
        formeJuridique: values.formeJuridique || null,
        numNat: values.numNat || null,
        typeFormalite: values.typeFormalite || null,
        providerRef: values.providerRef || null,
        status: values.status || null,
        regularisation: values.regularisation,
        content: Object.keys(content).length > 0 ? content : null,
      },
    })
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <FormField>
          <FormLabel>Statut</FormLabel>
          <FormInput value={values.status} onChange={set('status')} />
        </FormField>
        <FormField>
          <FormLabel>SIREN</FormLabel>
          <FormInput
            value={values.siren}
            onChange={set('siren')}
            maxLength={9}
            pattern="\d{9}"
            placeholder="9 chiffres"
          />
        </FormField>
        <FormField>
          <FormLabel>Société</FormLabel>
          <FormInput value={values.companyName} onChange={set('companyName')} />
        </FormField>
        <FormField>
          <FormLabel>Forme juridique</FormLabel>
          <FormInput
            value={values.formeJuridique}
            onChange={set('formeJuridique')}
          />
        </FormField>
        <FormField>
          <FormLabel>Num. nat.</FormLabel>
          <FormInput value={values.numNat} onChange={set('numNat')} />
        </FormField>
        <FormField>
          <FormLabel>Type formalité</FormLabel>
          <FormInput
            value={values.typeFormalite}
            onChange={set('typeFormalite')}
          />
        </FormField>
        <FormField>
          <FormLabel>Réf. provider</FormLabel>
          <FormInput value={values.providerRef} onChange={set('providerRef')} />
        </FormField>
        <FormField>
          <FormLabel>Régularisation</FormLabel>
          <FormCheckbox
            type="checkbox"
            checked={values.regularisation}
            onChange={e =>
              setValues(v => ({ ...v, regularisation: e.target.checked }))
            }
          />
        </FormField>
      </FormGrid>

      {Object.keys(content).length > 0 && (
        <ContentSection>
          <FormLabel>Contenu</FormLabel>
          <ContentEditorWrapper>
            <DepositContentEditor content={content} onChange={setContent} />
          </ContentEditorWrapper>
        </ContentSection>
      )}

      {error && <FormError>{error.message}</FormError>}

      <FormActions>
        <SaveButton type="submit" disabled={loading}>
          {loading ? 'Enregistrement…' : 'Enregistrer'}
        </SaveButton>
        <CancelButton type="button" onClick={onCancel}>
          Annuler
        </CancelButton>
      </FormActions>
    </Form>
  )
}

export default DepositEditForm

// ─── Styles ───────────────────────────────────────────────────────────────────

const baseButtonStyles = css`
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
`

const CancelButton = styled.button`
  ${baseButtonStyles}
  background: #fff1f0;
  color: #ff4d4f;

  &:hover {
    background: #ffd8d8;
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FormLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const FormInput = styled.input`
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: #171a1c;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #1677ff;
  }
`

const FormCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin-top: 4px;
`

const FormError = styled.p`
  color: #ff4d4f;
  font-size: 13px;
  margin: 0;
`

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ContentEditorWrapper = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 12px;
  background: #fafafa;
  max-height: 520px;
  overflow-y: auto;
`

const FormActions = styled.div`
  display: flex;
  gap: 8px;
`

const SaveButton = styled.button`
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #0958d9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
